import type { PhysicalProperties } from './physics/materialPhysicalProperties'

/**
 * ブラウザ内「おためしプレイ」エンジンの共有型定義。
 *
 * これは Roblox Studio のプレビューではなく、子どもが組んだ Blockly を
 * Studio とは独立にブラウザ内で近似実行するための自前ランタイムの契約である。
 * Studio の物理エンジン・Humanoid を忠実に再現するものではない（割り切り）。
 */

export type Vec3 = {
  readonly x: number
  readonly y: number
  readonly z: number
}

/**
 * 単位クォータニオン (x, y, z, w)。Part の完全な向き（X/Z 傾き含む）を
 * Euler 順序の取り違えなく three / rapier の双方へ渡すために使う。
 */
export type Quat = {
  readonly x: number
  readonly y: number
  readonly z: number
  readonly w: number
}

/** 無回転（単位）クォータニオン。 */
export const IDENTITY_QUAT: Quat = { x: 0, y: 0, z: 0, w: 1 }

/**
 * 対応する形状。Roblox の Part.Shape / WedgePart に対応する。
 * - box: 直方体（Block / Seat / Truss / Mesh フォールバック等）
 * - ball: 球（Part.Shape=Ball）。size を直径として等方スケール。
 * - cylinder: 円柱（Part.Shape=Cylinder）。Roblox は軸=X、長さ=size.x、直径=size.y(=size.z)。
 * - wedge: くさび（WedgePart / Part.Shape=Wedge）。+Z 側が全高、-Z へ下る斜面。
 * - cornerWedge: 隅くさび（CornerWedgePart / Part.Shape=CornerWedge）。1 つの上隅へ向かう斜面。
 */
export type ShapeKind = 'box' | 'ball' | 'cylinder' | 'wedge' | 'cornerWedge'

/**
 * Roblox の Decal/Texture を貼る面（Enum.NormalId 相当）。box の各面に対応する。
 * Roblox 既定の面法線: front=-Z, back=+Z, right=+X, left=-X, top=+Y, bottom=-Y。
 */
export type RobloxFace = 'front' | 'back' | 'right' | 'left' | 'top' | 'bottom'

/**
 * box の 1 面に貼る画像（Roblox Decal / Texture 由来）。
 * - Decal: 面全体に 1 枚（studsPerTile* は undefined）。
 * - Texture: タイル状に繰り返す（studsPerTile* に 1 タイルの stud 寸法）。
 */
export interface FaceDecal {
  readonly face: RobloxFace
  /** 取得済みテクスチャ画像の URL（CDN 取得成功時のみ。401 等は変換側で除外）。 */
  readonly textureUrl: string
  /** Texture のタイル U 方向間隔(stud)。Decal は undefined。 */
  readonly studsPerTileU?: number
  /** Texture のタイル V 方向間隔(stud)。Decal は undefined。 */
  readonly studsPerTileV?: number
  /**
   * 画像の透明度（Roblox の Decal/Texture.Transparency、0=不透明〜1=透明）。
   * 省略時 0。例: RJF07 の床タイルは 0.8 で下地の Part 色にうっすら重なる。
   */
  readonly transparency?: number
}

/**
 * box の 1 面に貼る固定テキスト看板（Roblox の SurfaceGui > TextLabel 由来）。
 * 例: RJS04 の外野フェンス Score 看板（ヒット/アウト/ツーベース/ホームラン！）。
 * 画像（FaceDecal）ではなく文字・前景/背景色を持ち、レンダラが CanvasTexture へ
 * 焼き込んで面へ貼る。
 */
export interface FaceSurfaceText {
  readonly face: RobloxFace
  /** 表示する文言（TextLabel.Text）。 */
  readonly text: string
  /** 文字色の CSS 16 進カラー（TextLabel.TextColor3 由来）。 */
  readonly color: string
  /** 背景色の CSS 16 進カラー（BackgroundColor3 由来）。背景透明時は省略。 */
  readonly background?: string
}

export type SceneScriptClassName = 'Script' | 'LocalScript'

/**
 * rbxl に含まれる実行系 Script のメタデータ。
 *
 * ブラウザプレビューは Luau を eval せず、preview/behaviors/scriptBindings.ts が
 * 既知で安全なテンプレート用パターンだけを MissionBehaviors へ写す。
 */
export interface SceneScriptDescriptor {
  readonly name: string
  readonly className: SceneScriptClassName
  readonly source: string
  /** Roblox の Script.Disabled。true の Script は実行対象外。 */
  readonly disabled?: boolean
}

/**
 * 対応する Roblox マテリアル（UI・rbxlx エクスポートで共有）。
 * looks_materials ドロップダウンの全候補（実在の Enum.Material 名）を網羅し、
 * プレビューは素材ごとに固有の模様・質感で描き分ける。
 */
export const ROBLOX_MATERIALS = [
  'Plastic',
  'SmoothPlastic',
  'Neon',
  'Wood',
  'WoodPlanks',
  'Marble',
  'Basalt',
  'Slate',
  'CrackedLava',
  'Concrete',
  'Limestone',
  'Granite',
  'Pavement',
  'Brick',
  'Pebble',
  'Cobblestone',
  'Rock',
  'Sandstone',
  'CorrodedMetal',
  'DiamondPlate',
  'Foil',
  'Metal',
  'Grass',
  'LeafyGrass',
  'Sand',
  'Fabric',
  'Snow',
  'Mud',
  'Ground',
  'Asphalt',
  'Salt',
  'Ice',
  'Glacier',
  'Glass',
  'ForceField',
  'Air',
  'Water',
  'Cardboard',
  'Carpet',
  'CeramicTiles',
  'ClayRoofTiles',
  'RoofShingles',
  'Leather',
  'Plaster',
  'Rubber',
] as const

export type RobloxMaterial = (typeof ROBLOX_MATERIALS)[number]

/**
 * Part 色に追従して発光するマテリアル。バッチ描画（単一マテリアル共有の
 * InstancedMesh）では per-instance の emissive を表現できないため、
 * バッチ対象から除外する判定に使う（レンダラとバッチャで共有）。
 */
export const EMISSIVE_COLOR_MATERIALS: ReadonlySet<RobloxMaterial> = new Set([
  'Neon',
  'ForceField',
])

/** World 状態・描画で使う、操作アバターの予約 ID。 */
export const AVATAR_ID = '__avatar__'

/**
 * 子どもがキー操作する簡易アバターの記述。
 * Roblox の Humanoid を忠実再現するものではなく、キネマティックな近似。
 */
export interface AvatarDescriptor {
  /** スポーン位置（中心、Y-up）。 */
  spawn: Vec3
  /** アバターの寸法（箱近似）。 */
  size: Vec3
  /** 水平移動速度（studs/秒）。 */
  speed: number
  /** 表示色。省略時はレンダラ既定。 */
  color?: string
  /**
   * スポーン時の向き（Y ヨー、ラジアン）。実機は SpawnLocation の向きで
   * スポーンする（例: RJF03 は 180 度）。省略時 0（-Z 向き）。
   */
  spawnYaw?: number
}

/**
 * シーンを構成する 1 オブジェクトの静的な記述（手書き）。
 * `.rbxl` を解析する代わりに、ミッションごとにこの記述を用意する。
 */
export interface SceneObjectDescriptor {
  /** コード・状態管理で使う安定 ID（ASCII）。例: 'pinkWall'。 */
  id: string
  /**
   * ミッション定義の `MissionData.objects[].name` と一致させる名前。
   * 例: '①ピンクのかべ'。これで Blockly workspace と紐づける。
   */
  name: string
  shape: ShapeKind
  /** 初期位置（Roblox 同様 Y-up）。 */
  position: Vec3
  /** 各軸の寸法。 */
  size: Vec3
  /** CSS 16 進カラー。例: '#ff69b4'。 */
  color: string
  /** Roblox マテリアル。省略時は 'Plastic'。 */
  material?: RobloxMaterial
  /** rbxl の CustomPhysicalProperties（未指定時は材質の既定値）。 */
  physicalProperties?: PhysicalProperties
  /** 箱の代わりに読み込む glTF(.glb) モデルの URL。省略時は箱で描画。 */
  modelUrl?: string
  /** Roblox 形式の .mesh ファイル URL（rbxl 由来 MeshPart / SpecialMesh で使用、glTF より優先）。 */
  meshUrl?: string
  /**
   * メッシュの表示スケール（SpecialMesh.Scale 由来）。省略時は等倍。
   * MeshPart はネイティブ寸法で描画するが、Part の子 SpecialMesh(FileMesh) は
   * Scale で拡大して表示されるため、その倍率をレンダラへ渡す。
   */
  meshScale?: Vec3
  /**
   * メッシュの表示オフセット（SpecialMesh.Offset 由来、Part ローカルの studs）。
   * 省略時は 0。メッシュ原点を Part 中心からずらして表示する SpecialMesh に使う。
   */
  meshOffset?: Vec3
  /**
   * MeshPart の TextureContent/TextureID 由来の画像 URL（rbxl 由来 MeshPart で使用）。
   * 指定時はメッシュ材質の albedo マップに適用する。Roblox に合わせ、テクスチャの
   * 読込成功時は Part.Color を乗算しない（テクスチャ表示が Color を上書きする）。
   * 未指定・読込失敗時は Part.Color のみ。
   */
  textureUrl?: string
  /**
   * テクスチャのアルファをカットアウト（alphaTest で透明部を破棄）するか。
   * Roblox は SurfaceAppearance.AlphaMode=Transparency のときだけ透明部を抜き、
   * 素の TextureID はアルファを無視して不透明描画する（例: 巨大樹の幹は
   * アルファ 0 の単色テクセルを参照しており、抜くと消えてしまう）。
   */
  textureCutout?: boolean
  /**
   * 初期 Y 軸回転（ラジアン）。省略時は 0。子の操作（turnRight 等）の初期ヨー。
   * rbxl 由来の完全な向きは `orientation` に持たせ、本値は実行時ヨーの初期値（通常 0）。
   */
  rotationY?: number
  /**
   * rbxl 由来の基準向き（X/Z 傾き含む完全な姿勢）。省略時は単位（無回転）。
   * 最終的な描画/当たり判定の向きは「Y ヨー(rotationY) ∘ orientation」で合成する。
   * これにより傾いた壁・ランプの向きを保持しつつ、子の Y 回転も両立できる。
   */
  orientation?: Quat
  /** 床など、プログラムを持たず動かない前提のオブジェクトか。 */
  isStatic?: boolean
  /**
   * 生成時から非 Anchored（isStatic=false）でも、最初から動的剛体（重力で
   * 落下・転がり・押せる）にしてよいか。物理側は通常、生成時から非 Anchored の
   * 部品をウェルド未対応の集合体（車体等）の崩壊回避のため Fixed のまま据え置く。
   * 本フラグは「単体の自由な物理オブジェクト（例: RJS01 の ①1つめのボール）」
   * だけを明示的に動的化する opt-in。省略時 false。
   */
  dynamicAtBirth?: boolean
  /**
   * 生成時から非 Anchored（isStatic=false）でも、プレビューでは「うごくようにする」
   * （movement_unsetAnchored）が実行されるまで固定扱いにし、解除されたときだけ
   * 動的剛体にする opt-in。dynamicAtBirth と違い、ブロック無しでは動かない
   * （例: RJF12 の ①ボール。教材上「うごくようにする」を付けて初めて転がる）。
   * World の初期 anchored と物理側の「生成時 Anchored」判定を true にする。省略時 false。
   */
  dynamicOnUnanchor?: boolean
  /** 初期透明度 (0=不透明, 1=完全透明)。省略時は 0。rbxl 由来シーンの半透明壁等で使う。 */
  transparency?: number
  /**
   * rbxl 由来の CanCollide。省略時は World 側で「完全透明=非衝突」の従来推定に
   * フォールバックする。装飾用 (CanCollide=false) のパーツをアバターがすり抜けられる
   * ようにするため、変換時に明示的に持たせる。
   */
  canCollide?: boolean
  /**
   * rbxl 由来の CanTouch。省略時は true（Roblox 既定）。CanCollide=false でも
   * CanTouch=true なら Roblox の Touched は発火するため、物理側でセンサーとして
   * 接触検知に残す（トリガーゾーンの onTouchCharacter を効かせる）。
   */
  canTouch?: boolean
  /**
   * 面に貼る画像（rbxl 由来の Decal/Texture）。box でのみ面別に適用する。
   * 取得できたテクスチャだけを持つ（CDN 401 等は変換側で除外済み）。省略時は無し。
   */
  decals?: FaceDecal[]
  /**
   * 面に貼る固定テキスト看板（SurfaceGui > TextLabel 由来）。box のみ対応。
   * 例: RJS04 の外野フェンス Score 看板。
   */
  surfaceText?: FaceSurfaceText[]
  /**
   * 効果音（rbxl 由来の子 Sound、Lua: script.Parent.Sound）。url は変換時に
   * 取得した音声ファイル、volume は Sound.Volume の初期値（Roblox は 0〜10）。
   * 「音を鳴らす」「音量」系ブロックの対象。省略時は音なし。
   */
  sound?: { url: string; volume: number }
  /**
   * キラキラエフェクト（rbxl 由来の子 Sparkles、Enabled=true のみ）。
   * レンダラがパーティクルを重ねる（例: RJF08 のワープブロック）。省略時は無し。
   */
  sparkle?: boolean
  /**
   * 炎エフェクト（rbxl 由来の子 Fire、Enabled=true のみ）。
   * レンダラがパーティクルを重ねる（例: RJF08 のトラップ球）。省略時は無し。
   */
  fire?: boolean
  /**
   * CollectionService タグ（rbxl 由来、ソート済み）。外部 rbxl の汎用ギミック
   * バインディング（タグ → ランタイム挙動）に使う。省略時はタグ無し。
   */
  tags?: readonly string[]
  /**
   * rbxl でこの BasePart 直下に置かれていた Script/LocalScript。
   * 任意実行はせず、既知パターンだけを preview behavior に変換する。
   */
  scripts?: readonly SceneScriptDescriptor[]
}

/** ビルダーで新規追加・control_clone で複製するオブジェクトの仕様。 */
export interface NewObjectSpec {
  id: string
  name: string
  position: Vec3
  size: Vec3
  color: string
  material?: RobloxMaterial
  /** カスタム物理特性。未指定時は material の既定値。 */
  physicalProperties?: PhysicalProperties
  /** 実行時ヨー（複製元の現在ヨーを引き継ぐ。省略時 0）。 */
  rotationY?: number
  /** 基準向き（複製元の現在姿勢を引き継ぐ。省略時は単位）。 */
  orientation?: Quat
  /** 初期透明度（複製元を引き継ぐ。省略時 0）。 */
  transparency?: number
  /** 衝突可否（省略時は World 既定）。 */
  canCollide?: boolean
  /** 接触検知（Touched）可否（省略時 true）。false は純粋な演出物向け。 */
  canTouch?: boolean
  /** 固定（Anchored）か（省略時は World 既定）。 */
  isStatic?: boolean
  /**
   * 描画形状（省略時 box）。物理コライダーの形状にも反映される
   * （実行時生成オブジェクトにも本体と同様の当たり判定が付く）。
   */
  shape?: ShapeKind
  /** 炎エフェクト（Roblox Fire 相当）を表示するか（省略時 false）。 */
  fire?: boolean
  /** キラキラエフェクト（Roblox Sparkles 相当）を表示するか（省略時 false）。 */
  sparkle?: boolean
  /**
   * 動的剛体（isStatic=false）として生成するときの初速（studs/秒）。
   * Roblox の Part.Velocity を与えた発射体（例: RJF05 の砲弾）を再現する。
   * 物理側が動的化の瞬間に 1 度だけ適用する。isStatic=true では無視される。
   */
  initialVelocity?: Vec3
  /**
   * 当たり判定サイズの倍率（省略時 1）。1 未満で見た目より当たり判定を小さく
   * する。火の玉のように炎エフェクトで大きく見えるが、本体のコアに触れたときだけ
   * 判定したい部品に使う。物理（コライダー）にのみ効き、描画には影響しない。
   */
  collisionScale?: number
}

/**
 * TerrainDescriptor.heights における「地形なし（穴）」の番兵値。
 * 実在の地形高さとして現れない十分小さい値（studs）。
 */
export const TERRAIN_HOLE_HEIGHT = -1e9

/**
 * Roblox Terrain（ボクセル地形）の高さマップ記述。
 *
 * 変換スクリプトが SmoothGrid を解析して 4 studs セル単位の地表グリッドへ
 * 落とし込む。プレビューはこれを連続した曲面メッシュとして描画し（実機の
 * smooth terrain の見た目に寄せる）、物理は同じ三角形から trimesh コライダー
 * を作る。地形は静的な背景であり、Blockly の操作対象（World のオブジェクト）
 * ではない。
 *
 * メッシュ化の仕様（terrainGeometry.ts）: 頂点は各セルの中心に 1 つ置き、隣接
 * セル中心同士を結んで三角形を張る。そのためメッシュの XZ 範囲はグリッド外周
 * より半セル（cellSize/2）内側になる（外周はクリップ余白の海・遠景であり、
 * 見た目・歩行に影響しない）。width または depth が 1 のグリッドは三角形を
 * 張れないため地形なし扱いになる。
 */
/**
 * Terrain のボクセルグリッド（SmoothGrid の固体ボクセル）。
 * 等値面（marching cubes）で実機同様の地形面を生成するための生データ。
 * 水ボクセルは含まない（水面は TerrainDescriptor.water の平面で表現する）。
 */
export interface TerrainVoxels {
  /** グリッド最小ボクセル座標（1 ボクセル = cellSize studs。ワールド = 座標 × cellSize）。 */
  minX: number
  minY: number
  minZ: number
  /** 各軸のボクセル数。 */
  sizeX: number
  sizeY: number
  sizeZ: number
  /**
   * RLE ラン（[個数, 素材, 占有率] の三つ組 × N）。
   * 線形 index = (y * sizeZ + z) * sizeX + x（x 最速）。
   * 素材 0 = 空気、1 以上 = palette[素材 - 1]。占有率は 0〜255。
   */
  runs: number[]
}

export interface TerrainDescriptor {
  /** 1 セルの一辺（studs）。SmoothGrid のボクセルと同じ 4。 */
  cellSize: number
  /** グリッド原点（セル (0,0) の最小コーナー）のワールド X/Z（studs）。 */
  originX: number
  originZ: number
  /** X / Z 方向のセル数。 */
  width: number
  depth: number
  /**
   * セルごとの地表の高さ（studs、行優先: index = zIndex * width + xIndex）。
   * 地形が存在しないセルは TERRAIN_HOLE_HEIGHT（メッシュ・コライダーとも穴）。
   * 変換側（lune）の JSON 直列化が配列内の null を表現できないため番兵値を使う。
   */
  heights: number[]
  /** セルごとの素材インデックス（palette 参照）。穴セルは無視。 */
  materials: number[]
  /** 素材インデックス → 見た目（プレビュー素材と色）。 */
  palette: { material: RobloxMaterial; color: string }[]
  /**
   * 水面（海）。level は水位（studs）、x0..z1 は水面の矩形範囲（studs）。
   * 水は固形ではなく、アバターは水中で簡易遊泳になる。
   */
  water?: { level: number; x0: number; x1: number; z0: number; z1: number }
  /**
   * 等値面（marching cubes）生成用のボクセルグリッド。存在する場合、描画・物理は
   * heights ではなくこちらから実機同様の地形面を構築する（省略時は高さマップ）。
   */
  voxels?: TerrainVoxels
}

/**
 * 焼き込み三角形メッシュ（Union=CSG の実形状など）。Blockly の操作対象ではない
 * 静的な背景ジオメトリで、レンダラは単色メッシュとして描画し、物理は同じ三角形
 * から trimesh コライダーを作る（見た目と当たり判定が常に一致する）。
 * 頂点はワールド座標（変換スクリプトが配置・回転・スケールを適用済み）。
 */
export interface StaticMeshDescriptor {
  name: string
  color: string
  material?: RobloxMaterial
  /** ワールド座標の頂点 (x, y, z) フラット配列。 */
  positions: number[]
  /** 三角形インデックス（positions の頂点番号）。 */
  indices: number[]
}

/**
 * 運転できる車両（rbxl の VehicleSeat を持つモデル）の記述。変換器が
 * VehicleSeat とその所属モデル（例: RJF01 の Sports Car）の部品を検出して
 * 出力する。シーン JSON にはウェルド/コンストレイント情報が無いため、
 * プレビューでは partIds の部品一式を 1 つの剛体として kinematic に動かす
 * ことでウェルド結合を代替する。
 */
export interface VehicleDescriptor {
  /** 運転席（VehicleSeat）のオブジェクト id。 */
  readonly seatId: string
  /** 車両を構成する全パーツのオブジェクト id（seatId を含む）。 */
  readonly partIds: ReadonlyArray<string>
  /**
   * 降車位置（座席ローカル座標、rbxl の ExitPosition Attachment 由来）。
   * 省略時は座席の左横（物理側の既定値）。
   */
  readonly exitOffset?: Vec3
  /**
   * 最高速度（studs/秒）。rbxl のモデル Attribute MaxSpeed（mph）を変換時に
   * studs/秒へ換算した値。省略時は endorsed vehicle テンプレートの既定相当。
   */
  readonly maxSpeed?: number
  /** 後退の最高速度（studs/秒）。maxSpeed と同様に換算済み。 */
  readonly reverseSpeed?: number
}

/** 1 ミッション分のシーン記述。 */
export interface SceneDescriptor {
  missionId: string
  title: string
  objects: SceneObjectDescriptor[]
  /** 操作アバター（省略時は閲覧のみ）。 */
  avatar?: AvatarDescriptor
  /** カメラの初期フレーミング（省略時はレンダラが自動算出）。 */
  camera?: { position: Vec3; target: Vec3 }
  /** Roblox Terrain の高さマップ（省略時は地形なし）。 */
  terrain?: TerrainDescriptor
  /** 焼き込み静的メッシュ（Union の実形状など。省略時はなし）。 */
  staticMeshes?: StaticMeshDescriptor[]
  /**
   * BGM（rbxl で Looped かつ Playing の Sound）。プレイ開始でループ再生し、
   * 停止で止める。省略時は BGM なし。
   */
  bgm?: { url: string; volume: number }
  /**
   * ライティング（Roblox の Lighting）。clockTime は時刻（0〜24）で、夜帯
   * （Roblox 慣習で 18 時〜翌 6 時）では夜空・月・星の表示に切り替える。
   * 省略時は既定の昼として描画する。
   */
  lighting?: { clockTime: number }
  /**
   * 運転できる車両（rbxl の VehicleSeat を持つモデル）。省略時は車両なし。
   * 物理側が乗車（E キー）・運転（WASD）・降車を提供する。
   */
  vehicles?: VehicleDescriptor[]
}

/**
 * 1 オブジェクトの実行時の可変状態。毎フレーム描画に反映される。
 * 透明度は Roblox の Transparency 慣習（0=不透明, 1=完全透明）に合わせる。
 */
export interface ObjectRuntimeState {
  readonly id: string
  /** 表示名（Explorer 表示・rbxlx の Part 名に使う）。 */
  readonly name: string
  readonly position: Vec3
  /** Y 軸ヨー（ラジアン）。子の turnRight/Left 等で変化する実行時の回転量。 */
  readonly rotationY: number
  /**
   * 基準向き（通常は rbxl 由来で不変、X/Z 傾きを含む完全姿勢）。最終姿勢は
   * 「Y ヨー(rotationY) ∘ orientation」で合成する（描画・物理の双方）。
   * 例外: アンカー解除した動的剛体では物理側が毎ステップ現在姿勢（タンブリング含む）を
   * ここへ書き込み、rotationY=0 とする（setDynamicTransforms）。
   */
  readonly orientation: Quat
  /** 0=不透明, 1=完全透明。 */
  readonly transparency: number
  readonly visible: boolean
  readonly color: string
  readonly size: Vec3
  /** Roblox マテリアル。 */
  readonly material: RobloxMaterial
  /** rbxl の CustomPhysicalProperties。未指定時は material の既定値（materialPhysicalProperties）。 */
  readonly physicalProperties?: PhysicalProperties
  /**
   * 描画形状。descriptor 由来オブジェクトは descriptor.shape、実行時生成
   * （addObject: clone / ミッションビヘイビア）は spec.shape を引き継ぐ（既定 box）。
   */
  readonly shape: ShapeKind
  /**
   * 炎エフェクト（Roblox の Fire 子インスタンス相当）を表示するか。
   * レンダラがパーティクルを重ねる（物理・当たり判定には影響しない）。
   */
  readonly fire: boolean
  /**
   * キラキラエフェクト（Roblox の Sparkles 子インスタンス相当）を表示するか。
   * fire と同じくレンダラ専用で、物理・当たり判定には影響しない。
   */
  readonly sparkle: boolean
  /** 衝突するか（Roblox CanCollide 相当）。物理側の collider 有効/無効に対応。 */
  readonly canCollide: boolean
  /**
   * 接触検知するか（Roblox CanTouch 相当）。false なら物理側の sensor 登録も
   * 行わない（純粋な演出物。例: RJF01 の火の玉・火の粉）。
   */
  readonly canTouch: boolean
  /**
   * 当たり判定サイズの倍率（既定 1）。1 未満でコライダーを見た目より小さくする。
   * 物理（コライダー）にのみ効き、描画サイズ（size）には影響しない。
   */
  readonly collisionScale: number
  /** クリック可能か（Roblox ClickDetector 相当）。raycast 対象を絞る。 */
  readonly clickable: boolean
  /**
   * アンカー固定か（Roblox Anchored 相当）。true=固定、false=重力で動く動的剛体。
   * setAnchored/unsetAnchored で変化し、物理側が fixed↔dynamic を切り替える。
   */
  readonly anchored: boolean
  /**
   * 動的剛体として生成されたときの初速（studs/秒）。addObject の
   * NewObjectSpec.initialVelocity 由来で、物理側が動的化の瞬間に 1 度だけ
   * 適用する（以降の実速度はここへは反映されない）。
   */
  readonly initialVelocity?: Vec3
  /**
   * 現在の線速度（studs/秒）。動的剛体のときに物理側が毎ステップ書き戻す
   * （setDynamicTransforms）。ビヘイビアが現在速度を読む用途
   * （例: RJS04 のバット接触でボール速度を 5 倍にする）に使う。
   * 非動的・未動的化のオブジェクトでは undefined。再アンカー後は直近の動的値が
   * 残り得る（読み手は anchored で除外すること）。
   */
  readonly velocity?: Vec3
  /**
   * 現在の角速度（rad/秒）。動的剛体のときに物理側が毎ステップ書き戻す
   * （setDynamicTransforms）。Lua の AssemblyAngularVelocity に対応する。
   * 非動的・未動的化のオブジェクトでは undefined。再アンカー後は直近の動的値が
   * 残り得る（読み手は anchored で除外すること）。
   */
  readonly angularVelocity?: Vec3
  /**
   * 効果音の現在音量（Roblox Sound.Volume 相当、0〜10）。descriptor.sound を
   * 持つオブジェクトのみ定義され、音量ブロック（sound_setVolumeTo 等）で
   * 実行時に変化する。undefined は「Sound を持たない」を表す。
   */
  readonly soundVolume?: number
}

export type WorldChangeListener = (
  states: ReadonlyMap<string, ObjectRuntimeState>,
) => void

export interface ObjectVelocityRequest {
  linear?: Vec3
  angular?: Vec3
}

/**
 * シーンの「見た目・空間」状態を保持する World の契約。
 *
 * - インタプリタ（実行エンジン）が mutation メソッドを呼ぶ。objectId は実行中の `self`。
 * - レンダラ（three.js）が `subscribe` で状態変化を購読して描画する。
 * - メッセージ/イベントの配送は World ではなく Runtime が担う（関心の分離）。
 */
export interface PreviewWorld {
  /** 現在の全オブジェクト状態（読み取り専用ビュー）。 */
  getState(): ReadonlyMap<string, ObjectRuntimeState>
  /** 状態変化の購読。返り値で解除。 */
  subscribe(listener: WorldChangeListener): () => void
  /** 全オブジェクトを記述の初期状態へ戻す。 */
  reset(): void

  /** 相対移動（moveSteps / 位置変更系）。 */
  moveBy(objectId: string, delta: Vec3): void
  /**
   * 絶対位置を設定（物理駆動アバター等、外部が位置を確定する場合）。
   * 動的剛体では速度を保持してテレポートする（Roblox の Position 代入）。
   */
  setPosition(objectId: string, position: Vec3): void
  /** Y 軸相対回転（turnRight/Left 系）。 */
  rotateYBy(objectId: string, radians: number): void
  /** Y 軸絶対回転を設定（アバターの向き等）。 */
  setRotationY(objectId: string, radians: number): void
  /**
   * アバターの位置とヨーを 1 回の通知でまとめて設定する。運転中の座席追従で
   * setPosition + setRotationY を別々に呼ぶと 1 物理ステップで通知が 2 回走り、
   * 購読側（レンダラ）の全件再同期が二重化するため、両者を 1 度の notify に集約する。
   */
  setAvatarPose(position: Vec3, yaw: number): void
  /**
   * 完全姿勢（X/Z 傾き含む）をクォータニオンで設定する。orientation を与値にし
   * rotationY は 0 に倒す（最終姿勢 = 与えたクォータニオンそのもの）。Blockly の
   * 向き系ブロック（setXYZOrientationTo / turnRight 等）が合成済み姿勢を渡す。
   */
  setOrientation(objectId: string, orientation: Quat): void
  /** 透明度設定（0..1 にクランプ）。 */
  setTransparency(objectId: string, transparency: number): void
  /** 表示/非表示（show/hide）。 */
  setVisible(objectId: string, visible: boolean): void
  /** 衝突可否（setCanCollide/unsetCanCollide）。物理 collider に反映される。 */
  setCanCollide(objectId: string, canCollide: boolean): void
  /**
   * 接触検知可否（Lua: BasePart.CanTouch）。false で Touched 相当の検知から
   * 除外される（環境ビヘイビアが ServerStorage 相当のテンプレートを不活性化
   * する用途）。物理側はモード判定・接触収集の両方で state を参照する。
   */
  setCanTouch(objectId: string, canTouch: boolean): void
  /** クリック可否（setClickable/unsetClickable）。 */
  setClickable(objectId: string, clickable: boolean): void
  /**
   * 効果音の音量を設定する（sound_setVolumeTo 等、Lua: Sound.Volume）。
   * Roblox の範囲 0〜10 にクランプする。Sound を持たない（soundVolume が
   * undefined の）オブジェクトには効かない。
   */
  setSoundVolume(objectId: string, volume: number): void
  /**
   * アンカー固定の設定（setAnchored/unsetAnchored）。false で重力落下する動的剛体に、
   * true で現在位置に固定する。物理側が fixed↔dynamic を切り替える。
   */
  setAnchored(objectId: string, anchored: boolean): void
  /**
   * 動的剛体の線速度を設定する（Lua: BasePart.AssemblyLinearVelocity 相当）。
   * state を更新し、物理側が次の同期（動的化時を含む）で要求を消費する。
   * 再アンカー時は速度退避前に適用し、固定のままの部品では要求を無視する。
   * setPosition と同じステップでも適用される（例: RJS04 のバット反発）。
   */
  setObjectVelocity(objectId: string, velocity: Vec3): void
  /**
   * 動的剛体の角速度を設定する（Lua: BasePart.AssemblyAngularVelocity 相当）。
   * state を更新し、物理側が次の同期（動的化時を含む）で要求を消費する。
   * 再アンカー時は速度退避前に適用し、固定のままの部品では要求を無視する。
   * setPosition と同じステップでも適用される。
   */
  setObjectAngularVelocity(objectId: string, angularVelocity: Vec3): void
  /** 未処理の速度変更要求を取り出して消費する（物理側専用）。 */
  consumeObjectVelocityRequest(objectId: string): ObjectVelocityRequest | null
  /**
   * 動的剛体の確定姿勢をまとめて反映する（物理側が毎ステップ呼ぶ）。位置と完全姿勢
   * （タンブリングを含むクォータニオン）を設定し rotationY は 0 に倒す。velocity は
   * 現在の線速度（ビヘイビアが読む用）。複数を 1 回の通知で反映するため、動的剛体が
   * 複数あっても購読側の再描画は 1 度で済む。
   */
  setDynamicTransforms(
    updates: ReadonlyArray<{
      objectId: string
      position: Vec3
      orientation: Quat
      velocity?: Vec3
      angularVelocity?: Vec3
    }>,
  ): void
  /**
   * アバターの歩行速度（stud/秒）を設定する。Blockly の events_setWalkSpeedTo
   * から呼ばれ、物理ステップで都度参照される。reset で descriptor 値に戻る。
   */
  setAvatarWalkSpeed(speed: number): void
  /** 現在のアバター歩行速度。物理コントローラが毎ステップ参照する。 */
  getAvatarWalkSpeed(): number
  /**
   * アバターのジャンプ力を設定する（events_setJumpPowerTo, Lua: humanoid.JumpPower）。
   * 物理コントローラがジャンプ初速として参照する。reset で既定値に戻る。
   */
  setAvatarJumpPower(power: number): void
  /** 現在のアバターのジャンプ力。物理コントローラがジャンプ時に参照する。 */
  getAvatarJumpPower(): number
  /**
   * アバターの体力を設定する（events_setHealthTo, Lua: humanoid.Health）。
   * 物理コントローラが 0 以下で spawn へ復帰させる。reset で最大値に戻る。
   */
  setAvatarHealth(health: number): void
  /** 現在のアバターの体力。物理コントローラが毎ステップ参照する。 */
  getAvatarHealth(): number
  /**
   * アバターの Humanoid 由来ステータス（歩行速度・ジャンプ力・体力）を既定へ
   * 戻す。実機の死亡リスポーンは Character/Humanoid の再生成なので、プログラム
   * による上書き（events_setWalkSpeedTo 等）はリスポーンで失効する。
   * 物理コントローラが死亡リスポーン時に呼ぶ。
   */
  resetAvatarStats(): void
  /**
   * アバターを指定位置へ強制テレポートする（ワープギミック用。Lua:
   * HumanoidRootPart.Position = ... 相当）。要求は次の物理ステップで
   * 物理コントローラが適用する。
   */
  requestAvatarTeleport(position: Vec3): void
  /** 物理コントローラ専用: 未処理のテレポート要求を取り出してクリアする。 */
  consumeAvatarTeleport(): Vec3 | null
  /** オブジェクトが存在するか（destroy 後は false）。 */
  exists(objectId: string): boolean
  /** オブジェクトをシーンから除去（control_destroy / ビルダー削除）。 */
  destroy(objectId: string): void
}

/**
 * ビルダー（編集）用に拡張した World 契約。
 * runtime/physics/renderer は読み取り＋実行時 mutation の `PreviewWorld` で足り、
 * オブジェクトの追加・寸法/色/マテリアル編集を要するのはビルダーのみ（SRP）。
 */
export interface EditablePreviewWorld extends PreviewWorld {
  /** 新規オブジェクトを追加する。 */
  addObject(spec: NewObjectSpec): void
  /** 寸法を設定する（0/負/非有限は正規化される）。 */
  setSize(objectId: string, size: Vec3): void
  /** 色を設定する（CSS 16 進）。 */
  setColor(objectId: string, color: string): void
  /** マテリアルを設定する。 */
  setMaterial(objectId: string, material: RobloxMaterial): void
}

/**
 * アバターの瞬時モーション状態。物理側（PhysicsController）で算出し、
 * レンダラ側で姿勢アニメ（屈伸・空中・着地）の合成に使う。
 *
 * - grounded: 物理側の接地判定（characterController.computedGrounded()）。
 *   レンダラ側で位置 Y 差分推定を使わずに済むので信頼性が上がる。
 * - verticalVelocity: 物理側で保持している鉛直速度。上昇/下降の見分けに利用する。
 */
export interface AvatarMotionState {
  readonly grounded: boolean
  readonly verticalVelocity: number
  /**
   * ダッシュ中判定。Shift を押しながら移動キーを入力している間 true。
   * 物理側は移動速度を倍率で引き上げ、レンダラ側は走行モーション（腕脚の振り幅・
   * 前傾・バウンス）を増幅するために参照する。
   */
  readonly isDashing: boolean
  /**
   * 簡易遊泳中判定（terrain.water の水域で足元が深く水没している間 true）。
   * レンダラ側は泳ぐポーズ（前傾・腕のかき・足の蹴り）へ切り替えるために参照する。
   */
  readonly isSwimming: boolean
  /**
   * 遊泳中かつ移動キー（WASD/矢印）の入力がある間 true。レンダラ側は
   * 平泳ぎ（ストローク）と浮遊（脱力）の切替に使う。移動量ではなく入力で
   * 判定するため、岸や壁に押し付けて進めない間も泳ぎ続けて見える。
   */
  readonly isSwimmingMoving: boolean
  /**
   * すべり台滑走中判定。「すべり台」という名前のオブジェクトの斜面に乗ると
   * true になり、操作不能で滑り降りる。レンダラは仰向けに倒れて滑るポーズへ
   * 切り替えるために参照する。
   */
  readonly isSliding: boolean
  /**
   * 乗車中判定（VehicleSeat に着席して運転している間 true）。レンダラは
   * 着席ポーズへ切り替え、歩行スイング・バウンス・空中ポーズを抑制する。
   */
  readonly isSeated: boolean
  /**
   * リスポーン/リセットで spawn へテレポートした回数（単調増加）。
   * 落下中(空中)→ spawn(接地) のテレポートをレンダラが「着地」と誤検出して
   * 屈伸沈み込みを発動し、足が spawn 台へめり込むのを防ぐために使う。レンダラは
   * 前フレームとの差分で「テレポート復帰フレーム」を判定する（フレーム落ちで物理が
   * 1 フレームに複数ステップ進んでも取りこぼさない）。
   */
  readonly respawnGeneration: number
  /**
   * 死亡演出中の種別（Roblox の RespawnTime 相当の 3 秒間）。null は生存。
   * - 'health': 体力 0。レンダラは倒れるポーズを表示する。
   * - 'void': 奈落落下。レンダラはアバターを非表示にし、カメラは消えた場所に留まる。
   */
  readonly deathKind: 'health' | 'void' | null
  /**
   * 体力 0（'health'）の倒れ込みパラメータ。health 死亡中のみ非 null。
   * 物理側が前方・後方の障害物までの距離を測って決める:
   * - direction: 空いている側へ倒れる向き
   * - maxAngleRad: 障害物に身体が届かない最大倒れ角（両側が狭い場所では
   *   90° まで倒さず途中で止め、壁などへの貫通を防ぐ）
   */
  readonly deathFall: {
    readonly direction: 'forward' | 'backward'
    readonly maxAngleRad: number
  } | null
  /**
   * 描画用の補間済みアバター位置。物理は固定ステップ（1/60s）、描画は別の
   * rAF で進むため、ステップ確定位置をそのまま描くと周期・位相のずれで歩行が
   * ガタつく。物理側が直近 3 ステップを保持し、表示時刻を最新ステップから
   * 2 ステップ（≒33ms）遅らせて区分線形補間した値を返す。レンダラは表示位置
   * にのみ使う（World の位置＝判定・スクリプトの真実源は物理確定位置のまま）。
   * null のときは World の位置で描く（死亡演出中の位置凍結など）。
   */
  readonly renderPosition: Vec3 | null
}

/**
 * おためしプレイの実行ランタイムの契約。
 * 複数オブジェクトのプログラムを束ね、onPlay / onMessage を配送する。
 */
export interface PreviewRuntime {
  /** onPlay を発火してプレイ開始。 */
  start(): void
  /** 実行を停止（進行中の wait などを中断）。 */
  stop(): void
  /** 停止し、World を初期状態へ戻す。 */
  reset(): void
  /** レンダラのクリック（レイキャスト命中）を実行系へ伝える。 */
  emitClick(objectId: string): void
  /** アバターがオブジェクトに接触したことを実行系へ伝える（onTouchCharacter 発火）。 */
  emitTouch(objectId: string): void
  /**
   * 後始末（実行停止・内部購読解除）。
   * workspaces は呼び出し側が所有する借用物であり、ここでは破棄しない
   * （生成元の責務。例: buildWorkspaces の利用側）。
   */
  dispose(): void
}
